import styled, { css } from "styled-components";
import {
    type DisplayProps,
    type MinWidthProps,
    type SpaceProps,
    type TypographyProps,
    type WidthProps,
    compose,
    display,
    minWidth,
    position,
    space,
    typography,
    width,
} from "styled-system";
import { type BreakPointValuesType, type ThemeTypedColorProps, getBreakpoint } from "../../themes/common";
import * as Type from "./Type";
import { IconAnimatedToggleArrowStyle } from "./AnimatedStyles";
import { IconWrapper } from "./IconWrapper";
import { theme } from "../v2/toyota/theme/toyotaTheme";

const hasIconStyles = css`
    display: inline-flex;
    align-items: center;
    svg {
        margin-right: 9px;
    }
    ${IconAnimatedToggleArrowStyle} {
        margin-right: 9px;
        svg {
            margin: 0;
        }
    }

    html[dir="rtl"] & {
        svg {
            margin-left: 9px;
            margin-right: 0;
        }
        ${IconAnimatedToggleArrowStyle} {
            margin-left: 9px;
            margin-right: 0;
        }
    }
`;

const hasIconRightStyles = css`
    display: inline-flex;
    align-items: center;
    svg {
        margin-left: 9px;
        margin-right: 0;
    }
    ${IconAnimatedToggleArrowStyle} {
        margin-left: 9px;
        margin-right: 0;
        svg {
            margin: 0;
        }
    }

    html[dir="rtl"] & {
        svg {
            margin-left: 0;
            margin-right: 9px;
        }
        ${IconAnimatedToggleArrowStyle} {
            margin-left: 0;
            margin-right: 9px;
        }
    }
`;

const hasOnlyIconStyles = css`
    align-items: center;
    justify-content: center;
    width: 36px;
    min-width: 0;
    height: 36px;
    padding: 11px !important;
    line-height: 2rem;
`;

export type ButtonType = {
    size?: BreakPointValuesType;
    hasIcon?: boolean;
    hasIconRight?: boolean;
    hasOnlyIcon?: boolean;
    cursor?: string;
    disabled?: boolean;
    notClickable?: boolean;
} & DisplayProps &
    WidthProps &
    MinWidthProps &
    SpaceProps &
    TypographyProps &
    ThemeTypedColorProps;

const styleProps = compose(display, width, minWidth, space, typography);

/* STANDARD BUTTON STYLES */

export const toyotaButtonStyles = css<ButtonType>`
    min-width: 160px;
    max-width: 100%;
    margin: 0;
    padding: ${(props) => (props.size && props.size === "sm" ? "4px 9px" : "10px 30px")};
    line-height: 1.5;
    color: ${theme.colors.primaryBlack};
    white-space: nowrap;
    background-color: ${theme.colors.grey1};
    border: none;
    border-radius: 100px;
    ${styleProps};
    ${(props) => props.hasIcon && hasIconStyles};
    ${(props) => props.hasIconRight && hasIconRightStyles};
    ${(props) => props.hasOnlyIcon && hasOnlyIconStyles};
    ${(props) =>
        props.notClickable &&
        css`
            pointer-events: none;
            cursor: auto;
        `};
    ${({ disabled }) =>
        disabled &&
        css`
            background-color: ${theme.colors.grey4} !important;
        `};
`;

const Button = styled.button<ButtonType>`
    ${toyotaButtonStyles};
`;

/* BUTTON TYPES */

export type PrimaryButtonType = { disabled?: boolean & TypographyProps & WidthProps & DisplayProps };
export const toyotaButtonPrimaryStyles = css<PrimaryButtonType>`
    color: ${({ disabled }) => (disabled ? theme.colors.grey1 : theme.colors.primaryWhite)};
    background-color: ${({ disabled }) => (disabled ? theme.colors.grey3 : theme.colors.primaryRed)};
    transition: background-color 0.3s;
    ${display};
    ${width};
    ${typography};
    ${space};
`;

export const lexusButtonPrimaryStyles = css<PrimaryButtonType>`
    color: ${({ disabled }) => (disabled ? theme.colors.grey5 : theme.colors.primaryWhite)};
    background-color: ${({ disabled }) => (disabled ? "transparent" : theme.colors.primaryRed)};
    border-color: ${({ disabled }) => (disabled ? theme.colors.grey2 : theme.colors.primaryRed)};
    &:visited {
        color: ${({ disabled }) => (disabled ? theme.colors.grey5 : theme.colors.primaryWhite)};
    }
    &::after {
        background: ${theme.colors.primaryWhite};
    }

    ${({ disabled }) =>
        disabled &&
        css`
            color: ${theme.colors.grey5} !important;
            background-color: transparent !important;
            &::after {
                display: none;
            }
        `};
`;

export const Primary = styled.button<ButtonType & PrimaryButtonType>`
    min-width: 160px;
    max-width: 100%;
    margin: 0;
    padding: ${(props) => (props.size && props.size === "sm" ? "4px 9px" : "10px 30px")};
    line-height: 1.5;
    white-space: nowrap;
    border: none;
    border-radius: 100px;
    ${(props) => props.hasIcon && hasIconStyles};
    ${(props) => props.hasIconRight && hasIconRightStyles};
    ${(props) => props.hasOnlyIcon && hasOnlyIconStyles};
    ${(props) =>
        props.notClickable &&
        css`
            pointer-events: none;
            cursor: auto;
        `};
    ${({ disabled }) =>
        disabled &&
        css`
            background-color: ${theme.colors.grey4} !important;
        `};
    ${(props) => (props.theme.isLexus ? lexusButtonPrimaryStyles : toyotaButtonPrimaryStyles)};
`;

export const toyotaButtonDarkStyles = css<PrimaryButtonType>`
    color: ${theme.colors.primaryWhite};
    background-color: ${theme.colors.primaryBlack};
`;

export const lexusButtonDarkStyles = css<PrimaryButtonType>`
    color: ${({ disabled }) => (disabled ? theme.colors.grey5 : theme.colors.primaryWhite)};
    background-color: ${({ disabled }) => (disabled ? "transparent" : theme.colors.primaryRed)};
    border-color: ${({ disabled }) => (disabled ? theme.colors.grey2 : theme.colors.primaryRed)};
    ${IconAnimatedToggleArrowStyle} {
        position: absolute;
        top: 50%;
        right: ${theme.space[3]}px;
        transform: translateY(-50%);
        svg {
            top: 0;
        }
    }
    &::after {
        display: none;
    }

    html[dir="rtl"] & {
        ${IconAnimatedToggleArrowStyle} {
            left: ${theme.space[3]}px;
            right: auto;
        }
    }
`;

export const Dark = styled(Button)`
    ${(props) => (props.theme.isLexus ? lexusButtonDarkStyles : toyotaButtonDarkStyles)};
`;

// Secondary Button (Core)
export const OutlinePrimary = styled(Button)`
    color: ${theme.colors.primaryRed};
    background-color: ${theme.colors.primaryWhite};
    border: 1px solid ${theme.colors.primaryRed};

    &:hover {
        color: ${theme.colors.primaryRedHover};
        border-color: ${theme.colors.primaryRedHover};
        box-shadow: inset 0 0 1px ${theme.colors.primaryRedHover};
    }
`;

export const Lightest = styled(Button)`
    background-color: ${theme.colors.primaryWhite};
    border: 1px solid ${theme.colors.grey2};
`;

export const Negative = styled(Button)<ButtonType>`
    color: ${theme.colors.primaryBlack};
    background-color: ${theme.colors.primaryWhite};
`;

export const Light = styled(Button)`
    color: ${theme.colors.primaryBlack};
    background-color: ${theme.colors.grey1};
`;

/* BUTTON ICON STYLES */

const linkHasIconStyles = css`
    display: inline-block;
    position: relative;
    padding-left: 28px; /* 20px (icon size) + 8px (spacing) */
    padding-right: 0;
    svg {
        position: absolute;
        top: 50%;
        left: 0;
        margin: 1px 0 0;
        transform: translateY(-50%);
    }
    &:hover {
        text-decoration: underline;
    }

    html[dir="rtl"] & {
        padding-left: 0;
        padding-right: 28px;
        svg {
            left: auto;
            right: 0;
        }
    }
`;

const linkHasIconRightStyles = css`
    position: relative;
    padding-left: 0;
    padding-right: 28px; /* 20px (icon size) + 8px (spacing) */
    svg {
        margin-top: 5px;
    }
    ${IconWrapper} {
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        svg {
            margin-top: 2px;
        }
    }

    html[dir="rtl"] & {
        padding-left: 28px;
        padding-right: 0;
        ${IconWrapper} {
            left: 0;
            right: auto;
        }
    }
`;

/* BUTTON STYLED AS A LINK */

type LinkType = {
    underline?: boolean;
    negative?: boolean;
    hasIconRight?: boolean;
    inlineIcon?: boolean;
    position?: string;
    hasLightColor?: boolean;
};

export const Link = styled.button<{ hasSmallText?: boolean } & ButtonType & LinkType>`
    min-width: 0;
    max-width: 100%;
    margin: 0;
    padding: 0;
    font-size: 1.5rem;
    line-height: 1.6;
    text-align: start;
    color: ${({ negative }) => (negative ? theme.colors.primaryWhite : theme.colors.primaryBlack)};
    white-space: nowrap;
    background-color: transparent;
    border: none;
    vertical-align: middle;
    ${position};
    ${space};

    ${(props) =>
        props.underline &&
        css`
            text-decoration: underline;
        `};
    ${(props) => props.hasIcon && linkHasIconStyles};
    ${(props) => props.hasIconRight && linkHasIconRightStyles};
    ${(props) =>
        props.theme.isLexus &&
        css`
            padding-left: 26px;
            font-family: ${theme.fonts.regular};
            font-size: 17px; /* lexus uses px values */
            text-transform: none;
            border: none;
            &::after {
                display: none;
            }
        `};
    ${({ inlineIcon }) =>
        inlineIcon &&
        css`
            padding-left: 0;
            svg {
                position: absolute;
                left: auto;
                right: auto;
                margin-left: ${theme.space[2]}px;
            }
        `};

    /* Fix style disclaimer */
    ${Type.Medium} + svg {
        position: absolute;
        left: 0;
    }

    ${({ hasSmallText }) =>
        hasSmallText &&
        css`
            font-size: 1.3rem;
            line-height: 1.8;
        `};

    ${({ hasLightColor }) =>
        hasLightColor &&
        css`
            color: ${theme.colors.grey4};
        `};

    &:disabled {
        text-decoration: none;
        background-color: transparent !important;
        opacity: 0.5;
    }

    html[dir="rtl"] & {
        ${(props) =>
            props.theme.isLexus &&
            css`
                padding-left: 0;
                padding-right: 26px;
            `};
        ${({ inlineIcon }) =>
            inlineIcon &&
            css`
                svg {
                    margin-left: 0;
                    margin-right: ${theme.space[2]}px;
                }
            `};
        ${Type.Medium} + svg {
            left: auto;
            right: 0;
        }
    }
`;

/* REMOVE BUTTON BASED ON BUTTON.LINK */

export const Remove = styled(Link)`
    display: flex;
    justify-content: center;
    padding: 13px;
    color: ${theme.colors.grey5};
    background-color: transparent;
    ${(props) =>
        props.theme.isLexus
            ? css`
                  justify-content: left;
                  margin-top: 10px;
                  padding-left: 26px;
                  color: ${theme.colors.primaryRed};
                  &::after {
                      display: none;
                  }
                  svg {
                      display: none;
                  }
              `
            : css`
                  svg {
                      margin-top: 3px;
                      margin-right: 4px;
                  }
              `};

    html[dir="rtl"] & {
        ${(props) =>
            props.theme.isLexus
                ? css`
                      padding-left: 0;
                      padding-right: 26px;
                  `
                : css`
                      svg {
                          margin-left: 4px;
                          margin-right: 0;
                      }
                  `};
    }
`;

/* CIRCLED BUTTON */

export const Round = styled.button`
    display: flex;
    position: relative;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    text-align: center;
    border: none;
    border-radius: 50%;
    svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

/* GREY COLOURED BUTTON */

export const Grey = styled(Button)`
    display: flex;
    max-height: ${(props) => (props.size === "sm" ? 36 : 44)}px;
    padding: ${(props) => (props.size === "sm" ? "8px 20px" : "10px 30px")};
    font-family: ${theme.fonts.base};
    font-size: 1.3rem;
    line-height: 1.5;
    color: ${theme.colors.primaryBlack};
    background-color: ${theme.colors.grey1};
    border-radius: 20px;
`;

/* CTA ICON WRAPPER */

export const CtaIconWrapper = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    min-width: 30px;
    height: 30px;
    margin-right: ${theme.space[3] / 2}px;
    color: ${theme.colors.primaryWhite};
    background-color: ${theme.colors.primaryRed};
    border-radius: 50%;

    html[dir="rtl"] & {
        margin-left: ${theme.space[3] / 2}px;
        margin-right: 0;
    }
`;

export const EllipsisText = styled.span<DisplayProps>`
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    ${display}
`;

export const Clamp = styled.span`
    display: block;
    min-width: 75px;
    max-height: 34px;
    line-height: 1;
    white-space: normal;
    @media ${getBreakpoint("down", "lg")} {
        width: 100%;
        min-width: auto;
        overflow: hidden;
        /* stylelint-disable */
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        /* stylelint-enable */
    }
`;

export default Button;
